import React, { useContext, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext, useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import { hasPermission, applyClinicAdminPrivileges } from '../../utils/loginRestrictions';
import { buildClinicSupportMailto } from '../../utils/supportMailto';
import SubscriptionAccessBanner from '../clinic/SubscriptionAccessBanner';
import EmailVerifyBanner from '../auth/EmailVerifyBanner';
import { reviewService } from '../../modules/googleReviews';
import { useTodayQueueCount } from '../../hooks/useTodayQueueCount';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  HomeIcon,
  UserGroupIcon,
  DocumentCheckIcon,
  UsersIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  PlusIcon,
  StarIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import './clinicShell.css';

const NAV_ITEMS = [
  { name: 'Dashboard', href: '/clinic', icon: HomeIcon, group: 'main' },
  { name: 'Patients', href: '/clinic/patients', icon: UserGroupIcon, group: 'main' },
  { name: 'Consents', href: '/clinic/consents', icon: DocumentCheckIcon, group: 'main' },
  { name: 'Messages', href: '/clinic/messages', icon: ChatBubbleLeftRightIcon, group: 'main' },
  {
    name: 'Reviews',
    href: '/clinic/reviews',
    icon: StarIcon,
    permission: 'accessSettings',
    group: 'practice',
  },
  {
    name: 'Review list',
    href: '/clinic/review-list',
    icon: EnvelopeIcon,
    permission: 'accessSettings',
    group: 'practice',
  },
  {
    name: 'Analytics',
    href: '/clinic/analytics',
    icon: ChartBarIcon,
    adminOnly: true,
    group: 'practice',
  },
  {
    name: 'Staff',
    href: '/clinic/staff',
    icon: UsersIcon,
    permission: 'manageStaff',
    group: 'practice',
  },
  {
    name: 'Settings',
    href: '/clinic/settings',
    icon: CogIcon,
    permission: 'accessSettings',
    group: 'practice',
  },
];

export default function ClinicLayout({ children }) {
  const { userData, refreshUserData } = useContext(AuthContext);
  const { signOut } = useAuth();
  const [clinicData, setClinicData] = useState(null);
  const todayQueueCount = useTodayQueueCount(userData?.clinicId);
  const [unreadReviewFeedback, setUnreadReviewFeedback] = useState(0);
  const [unreadReviewListFeedback, setUnreadReviewListFeedback] = useState(0);
  const [impersonation, setImpersonation] = useState(() => authService.getImpersonationSession());
  const [exitingImpersonation, setExitingImpersonation] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setImpersonation(authService.getImpersonationSession());
  }, [userData?.uid, location.pathname]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userData || userData.role !== 'clinic_admin' || userData.clinicId) return;
      try {
        const { ensureProvisionalClinic } = await import('../../utils/registrationFlow');
        await ensureProvisionalClinic({
          practiceName: userData.practiceName || userData.displayName || '',
        });
        if (!cancelled && refreshUserData) await refreshUserData();
      } catch (err) {
        console.warn('ensureProvisionalClinic:', err?.message || err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData?.uid, userData?.role, userData?.clinicId, refreshUserData]);

  useEffect(() => {
    if (!userData?.clinicId) {
      setClinicData(null);
      return undefined;
    }
    const unsub = onSnapshot(
      doc(db, 'clinics', userData.clinicId),
      (snap) => {
        if (snap.exists()) setClinicData(snap.data());
      },
      (error) => {
        console.error('Error loading clinic data:', error);
      }
    );
    return () => unsub();
  }, [userData?.clinicId]);

  useEffect(() => {
    if (!userData?.clinicId) {
      setUnreadReviewFeedback(0);
      setUnreadReviewListFeedback(0);
      return undefined;
    }
    return reviewService.subscribeUnreadFeedbackCount(userData.clinicId, (_reviews, split) => {
      setUnreadReviewFeedback(split?.reviews ?? _reviews ?? 0);
      setUnreadReviewListFeedback(split?.reviewList ?? 0);
    });
  }, [userData?.clinicId]);

  const effectiveUser = applyClinicAdminPrivileges(userData);

  const isActive = (href) => {
    if (href === '/clinic') return location.pathname === '/clinic';
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  const filteredNavigation = NAV_ITEMS.filter((item) => {
    if (effectiveUser?.role === 'clinic_admin') return true;
    if (item.permission) return hasPermission(effectiveUser, item.permission);
    if (item.adminOnly) return effectiveUser?.role === 'clinic_admin';
    return true;
  });

  const mainNav = filteredNavigation.filter((item) => item.group === 'main');
  const practiceNav = filteredNavigation.filter((item) => item.group === 'practice');

  const handleLogout = async () => {
    if (impersonation) {
      authService.clearImpersonationSession();
    }
    await signOut();
    navigate(impersonation ? '/admin-login' : '/login');
  };

  const handleExitImpersonation = async () => {
    setExitingImpersonation(true);
    try {
      await authService.endImpersonation();
      navigate('/admin-login');
    } catch (err) {
      console.error('Exit impersonation failed:', err);
      authService.clearImpersonationSession();
      navigate('/admin-login');
    } finally {
      setExitingImpersonation(false);
    }
  };

  const supportMailto = buildClinicSupportMailto({
    clinicName: clinicData?.name || userData?.clinicName,
    userName: userData?.displayName,
    userEmail: userData?.email,
  });

  const handleStartConsent = () => {
    setMobileMenuOpen(false);
    navigate('/clinic/consents/ai-generate');
  };

  const showStartConsent = hasPermission(effectiveUser, 'createConsents');

  const renderNavLink = (item) => {
    const Icon = item.icon;
    const showTodayBadge = item.href === '/clinic' && todayQueueCount > 0;
    const showUnreadBadge =
      (item.href === '/clinic/reviews' && unreadReviewFeedback > 0) ||
      (item.href === '/clinic/review-list' && unreadReviewListFeedback > 0);
    const unreadCount = showTodayBadge
      ? todayQueueCount
      : item.href === '/clinic/review-list'
        ? unreadReviewListFeedback
        : unreadReviewFeedback;
    return (
      <Link
        key={item.name}
        to={item.href}
        className={`clinic-nav-link${isActive(item.href) ? ' is-active' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      >
        <Icon className="h-5 w-5 mr-3" />
        <span className="flex-1">{item.name}</span>
        {showUnreadBadge || showTodayBadge ? (
          <span className="clinic-nav-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        ) : null}
      </Link>
    );
  };

  return (
    <div className="clinic-shell">
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          type="button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="clinic-mobile-toggle"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
        </button>
      </div>

      <div
        className={`clinic-sidebar fixed inset-y-0 left-0 z-40 w-64 transform transition-transform duration-300 ease-in-out ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="flex flex-col h-full min-h-0">
          <div className="flex-shrink-0 px-4 pt-6 pb-5">
            <Link to="/clinic" className="flex items-center gap-2.5 no-underline" onClick={() => setMobileMenuOpen(false)}>
              <span className="clinic-brand-mark" aria-hidden="true">
                <DocumentCheckIcon className="h-5 w-5" strokeWidth={2} />
              </span>
              <span className="clinic-brand-name">SmileConsent</span>
            </Link>
            <p className="clinic-clinic-name">
              {clinicData?.name ||
                userData?.clinicName ||
                userData?.practiceName ||
                userData?.displayName ||
                ''}
            </p>
            {showStartConsent && (
              <button type="button" onClick={handleStartConsent} className="clinic-start-consent">
                <PlusIcon className="h-5 w-5" />
                Start Consent
              </button>
            )}
          </div>

          <nav className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain px-3 pb-4 space-y-5">
            <div>
              <div className="clinic-nav-label">Clinic</div>
              <div className="space-y-1">{mainNav.map(renderNavLink)}</div>
            </div>

            {practiceNav.length > 0 && (
              <div>
                <div className="clinic-nav-label">Practice</div>
                <div className="space-y-1">{practiceNav.map(renderNavLink)}</div>
              </div>
            )}

            <div>
              <div className="clinic-nav-label">Account</div>
              <div className="space-y-1">
                <Link
                  to="/clinic/account"
                  className={`clinic-nav-link${isActive('/clinic/account') ? ' is-active' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <UserCircleIcon className="h-5 w-5 mr-3" />
                  My Account
                </Link>
                <a href={supportMailto} className="clinic-nav-link">
                  <QuestionMarkCircleIcon className="h-5 w-5 mr-3" />
                  Support
                </a>
              </div>
            </div>
          </nav>

          <div className="clinic-sidebar-footer flex-shrink-0">
            <div className="mb-3 px-1">
              <p className="text-sm font-semibold text-white truncate">
                {userData?.displayName || userData?.email}
              </p>
              <p className="text-xs text-slate-400 capitalize truncate">
                {userData?.email && userData?.displayName ? userData.email : userData?.role?.replace('_', ' ')}
              </p>
            </div>
            <button type="button" onClick={handleLogout} className="clinic-logout">
              <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="min-w-0 lg:pl-64">
        {impersonation && (
          <div className="sticky top-0 z-20 border-b border-amber-300 bg-amber-500 text-white px-4 py-2.5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 max-w-full">
              <div className="flex items-start gap-2 min-w-0">
                <ShieldCheckIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm font-medium leading-snug">
                  Superadmin support mode — viewing as{' '}
                  <span className="font-semibold">
                    {impersonation.clinicName || clinicData?.name || userData?.clinicName || 'clinic admin'}
                  </span>
                  . Sign in again at Admin Login when you exit.
                </p>
              </div>
              <button
                type="button"
                onClick={handleExitImpersonation}
                disabled={exitingImpersonation}
                className="flex-shrink-0 inline-flex items-center justify-center gap-1.5 rounded-md bg-white/15 hover:bg-white/25 px-3 py-1.5 text-sm font-semibold disabled:opacity-60"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
                {exitingImpersonation ? 'Exiting…' : 'Exit clinic portal'}
              </button>
            </div>
          </div>
        )}
        <EmailVerifyBanner />
        <SubscriptionAccessBanner userData={userData} clinicData={clinicData} />
        <main
          className={`min-h-screen min-w-0 max-w-full ${
            showStartConsent && !location.pathname.includes('/consents/ai-generate') ? 'pb-24 lg:pb-0' : ''
          }`}
        >
          {children}
        </main>
      </div>

      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {showStartConsent && !location.pathname.includes('/consents/ai-generate') && (
        <button
          type="button"
          onClick={handleStartConsent}
          className="clinic-fab lg:hidden fixed bottom-6 right-6 z-50"
          aria-label="Start Consent"
        >
          <PlusIcon className="h-5 w-5" />
          Start Consent
        </button>
      )}
    </div>
  );
}
